import { createContext, useContext, useState, useEffect } from "react";
import {
    onAuthStateChanged,
    signOut,
    updateProfile,
    sendPasswordResetEmail,
    GoogleAuthProvider,
    signInWithPopup,
} from "firebase/auth";
import { auth, db } from "../firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const googleProvider = new GoogleAuthProvider();

    // Google Sign-in (create Firestore user doc if new)
    const signInWithGoogle = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const firebaseUser = result.user;

            const userRef = doc(db, "users", firebaseUser.uid);
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) {
                // First time sign-in, create Firestore doc with uid
                await setDoc(userRef, {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    firstName: firebaseUser.displayName?.split(" ")[0] || "",
                    lastName: firebaseUser.displayName?.split(" ")[1] || "",
                    provider: "google",
                    createdAt: new Date(),
                });
            }

            // Merge Firestore data into local state
            const updatedSnap = await getDoc(userRef);
            setUser({ uid: firebaseUser.uid, ...firebaseUser, ...updatedSnap.data() });

        } catch (error) {
            console.error("Google sign-in failed:", error);
            throw error;
        }
    };

    // Listen to auth state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                let userData = {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    firstName: "",
                    lastName: "",
                };

                try {
                    const userRef = doc(db, "users", firebaseUser.uid);
                    const userSnap = await getDoc(userRef);

                    if (userSnap.exists()) {
                        userData = { ...userData, ...userSnap.data() };
                    } else {
                        // Firestore doc missing, fallback to displayName
                        const [first, ...rest] = (firebaseUser.displayName || "").split(" ");
                        userData.firstName = first || firebaseUser.email?.split("@")[0] || "User";
                        userData.lastName = rest.join(" ") || "";
                        // Optionally create Firestore doc for future use
                        await setDoc(userRef, {
                            uid: firebaseUser.uid,
                            email: firebaseUser.email,
                            firstName: userData.firstName,
                            lastName: userData.lastName,
                            provider: firebaseUser.providerData[0]?.providerId || "unknown",
                            createdAt: new Date(),
                        });
                    }
                } catch (error) {
                    console.error("Error fetching Firestore user:", error);
                    // fallback: populate user from firebaseUser even if permissions fail
                    const [first, ...rest] = (firebaseUser.displayName || "").split(" ");
                    userData.firstName = first || firebaseUser.email?.split("@")[0] || "User";
                    userData.lastName = rest.join(" ") || "";
                }

                setUser(userData);
            } else {
                setUser(null);
            }
        });

        return unsubscribe;
    }, []);


    // Logout
    const logoutUser = () => signOut(auth);

    // Update profile both in Firebase Auth and Firestore
    const updateUserProfile = async (updates) => {
        if (auth.currentUser) {
            await updateProfile(auth.currentUser, updates);
            const userRef = doc(db, "users", auth.currentUser.uid);
            await setDoc(userRef, updates, { merge: true });
            setUser((prev) => ({ ...prev, ...updates }));
        }
    };

    // Reset password
    const resetPassword = (email) => sendPasswordResetEmail(auth, email);

    return (
        <AuthContext.Provider
            value={{
                user,
                logoutUser,
                updateUserProfile,
                resetPassword,
                signInWithGoogle,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
